import React from 'react';
import { useMakeQuery } from 'src/api/queries/useMakeQuery';
import { authQueries } from 'src/api/queries';
import { render } from 'src/lib/utils';
import { requestClient } from 'src/lib/services/api/request-api';
import { UseQueryOptionsObject } from 'src/api/queries/queries.utils';
import { payloadHasScanPending } from 'src/features/media/media-scan';

const SCAN_POLL_INTERVAL_MS = 20 * 1000;
// The backend gives up on a scan at 90 min (plus its 5-min sweep), so this
// outlasts it and then stops.
const SCAN_POLL_MAX_MS = 120 * 60 * 1000;

type ApiResponse = Awaited<ReturnType<typeof requestClient.get>>;
type RequestQueryReturnType = ReturnType<typeof useMakeQuery<ApiResponse>>;

const RequestQueryContext = React.createContext<RequestQueryReturnType | undefined>(undefined);

const RequestQueryProvider: React.FC<{
  id: string;
  children: React.ReactNode | ((query: RequestQueryReturnType) => React.ReactNode);
  config?: UseQueryOptionsObject<ApiResponse>;
  /**
   * Refetch while a file on the request is still being scanned. Only the full
   * request pages opt in; previews and dialogs mount this provider too and
   * shouldn't each start their own poll.
   */
  pollWhileScanning?: boolean;
}> = ({ id, config = {}, pollWhileScanning = false, children }) => {
  // Nothing pushes a scan verdict to the page, so while a file on it (raw
  // footage, revisions, thumbnails, comment attachments) is still being
  // scanned, refetch until it clears. Capped so a hung scan doesn't poll
  // forever; the backend times it out first.
  const pendingSince = React.useRef<number | null>(null);
  React.useEffect(() => {
    pendingSince.current = null;
  }, [id]);
  const query = useMakeQuery([authQueries.request, id], () => requestClient.get(id), {
    suspense: true,
    retry: false,
    staleTime: 60 * 1000,
    refetchInterval: (data) => {
      if (!pollWhileScanning || !payloadHasScanPending(data)) {
        pendingSince.current = null;
        return false;
      }

      pendingSince.current ??= Date.now();
      return Date.now() - pendingSince.current < SCAN_POLL_MAX_MS ? SCAN_POLL_INTERVAL_MS : false;
    },
    ...config,
  });

  return (
    <RequestQueryContext.Provider value={query}>
      {render(children, query)}
    </RequestQueryContext.Provider>
  );
};

const useRequestQueryContext = () => {
  const context = React.useContext(RequestQueryContext);

  if (!context) {
    throw new Error('useRequestQuery must be used within a RequestQuery');
  }

  return context;
};

export { RequestQueryProvider, useRequestQueryContext };
